import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, Button, Flex, Heading, useMediaQuery } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import ResidentsTable from './ResidentsTable.tsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.tsx';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import AleaFilterTags from '../../components/AleaFilterTags/AleaFilterTags.tsx';
import SearchBar from '../../components/SearchBar/SearchBar';
import AleaDownloadButton, { DownloadOption } from '../../components/AleaDownloadButton/AleaDownloadButton';
import { debounce } from 'lodash';
import AleaListFilter from '../../components/AleaListFilter/AleaListFilter.tsx';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import { ResidentState } from '../../constants/ResidentState.ts';
import { AleaSelectedTags } from '../../components/AleaSelectedTag';
import { exportToPdf } from '../../utils/pdf/pdfExport';
import { UserRole } from '../../constants/UserRoles.ts';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';
import {
  useCareHomesForResidentsQuery,
  useResidentsListQuery,
  useStationsForResidentsQuery,
} from './residentsPage.queries.ts';
import { useCareHomeQuery } from '../CareHomePage/careHomePage.queries.ts';

const ResidentsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRestoring } = usePageFiltersStorage();
  const { user } = useAuth();
  const [isLargerThan1120] = useMediaQuery('(min-width: 1120px)');
  
  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();

  const DEFAULT_ORDER_BY = 'last_name';
  const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';
  
  // Store current search input value that comes directly from SearchBar
  const inputValueRef = useRef<string>('');
  
  // Extract query from URL search params
  const queryParams = new URLSearchParams(location.search);
  const urlQueryParam = queryParams.get('query') || '';
  const urlPageParam = parseInt(queryParams.get('page') || '1', 10);
  const urlPerPageParam = queryParams.get('perPage');
  const showArchived = queryParams.get('show_archived') === 'true';

  // State for search query - initialize from URL
  const [searchQuery, setSearchQuery] = useState(urlQueryParam);

  // State for current page - initialize from URL
  const [currentPage, setCurrentPage] = useState(urlPageParam);
  
  // State for perPage - If URL has perPage, use it, otherwise use stored value
  const [perPage, setPerPage] = useState(urlPerPageParam ? parseInt(urlPerPageParam, 10) : storedPerPage);

  // State for filter tag (Current/Archived) - initialize from URL
  const [selectedFilter, setSelectedFilter] = useState(showArchived ? 'archived' : 'current');

  const [isExporting, setIsExporting] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isRestoring) return;

    const params = new URLSearchParams(location.search);
    let shouldUpdate = false;

    if (!params.get('orderBy')) {
      params.set('orderBy', DEFAULT_ORDER_BY);
      shouldUpdate = true;
    }
    if (!params.get('sortOrder')) {
      params.set('sortOrder', DEFAULT_SORT_ORDER);
      shouldUpdate = true;
    }
    // Keep URL canonical so React Query queryKey stays stable
    if (!params.get('page')) {
      params.set('page', '1');
      shouldUpdate = true;
    }
    if (!params.get('perPage')) {
      params.set('perPage', storedPerPage.toString());
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [isRestoring, location.pathname, location.search, navigate, storedPerPage]);

  // Update states when URL changes
  useEffect(() => {
    const queryFromUrl = queryParams.get('query') || '';
    const pageFromUrl = parseInt(queryParams.get('page') || '1', 10);
    const perPageFromUrlRaw = queryParams.get('perPage');
    const perPageFromUrl = perPageFromUrlRaw ? parseInt(perPageFromUrlRaw, 10) : storedPerPage;
    const isArchived = queryParams.get('show_archived') === 'true';
    
    if (searchQuery !== queryFromUrl) {
      setSearchQuery(queryFromUrl);
    }
    
    if (currentPage !== pageFromUrl) {
      setCurrentPage(pageFromUrl);
    }

    if (perPage !== perPageFromUrl) {
      setPerPage(perPageFromUrl);
    }

    if (selectedFilter !== (isArchived ? 'archived' : 'current')) {
      setSelectedFilter(isArchived ? 'archived' : 'current');
    }
  }, [location.search, perPage, storedPerPage]);

  // Handle search input changes - this is called immediately when user types
  const handleSearch = (inputValue: string) => {
    // Store the latest input value in ref for debounce function to access
    inputValueRef.current = inputValue;
    debouncedUpdateUrl();
  };

  // Debounced function to update URL with the latest input value
  const debouncedUpdateUrl = useCallback(
    debounce(() => {
      // Use the current value from ref to ensure we have the latest input
      const latestInput = inputValueRef.current;
      const newSearchParams = new URLSearchParams(location.search);
      
      if (latestInput) {
        newSearchParams.set('query', latestInput);
      } else {
        newSearchParams.delete('query');
      }
      
      // Reset to page 1 when searching
      newSearchParams.set('page', '1');
      
      // Update the URL - this will trigger the fetchResidents via useEffect
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    }, 1000),
    [location.pathname, location.search, navigate]
  );

  // Clear search handler
  const handleClearSearch = () => {
    // Clear the ref value
    inputValueRef.current = '';
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('query');
    // Reset to page 1 when clearing search
    newSearchParams.set('page', '1');
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  const isQueryReady = useMemo(() => {
    if (isRestoring) return false;
    if (!queryParams.has('perPage')) return false;
    if (!queryParams.has('orderBy') || !queryParams.has('sortOrder')) return false;
    return true;
  }, [isRestoring, location.search, storedPerPage]);

  const residentsQueryInput = useMemo(
    () => ({
      search: location.search,
      userRole: user?.role,
      organisationId: user?.organisation?.id,
      userCareHomeId: user?.carehome?.id,
    }),
    [location.search, user?.carehome?.id, user?.organisation?.id, user?.role]
  );

  const residentsQuery = useResidentsListQuery(residentsQueryInput, isQueryReady);

  const stationsQuery = useStationsForResidentsQuery();
  const stations = stationsQuery.data ?? [];

  const careHomesQuery = useCareHomesForResidentsQuery(
    user?.organisation?.id,
    user?.role === UserRole.ORGANISATION_MANAGER
  );
  const careHomes = careHomesQuery.data?.items ?? [];

  const careHomeId =
    queryParams.get('carehome_id') || user?.carehome?.id || undefined;
  const { data: careHomeData } = useCareHomeQuery(careHomeId);
  const careHomeFeatures = careHomeData?.features ?? [];

  const isLoading = !isExporting && (residentsQuery.isLoading || residentsQuery.isFetching);

  // Make sure to clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateUrl.cancel();
    };
  }, [debouncedUpdateUrl]);

  const handleAddResidentClick = () => {
    navigate('/residents/add');
  };

  const handlePageChange = (page: number) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', page.toString());
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };
  
  // Handler for perPage changes
  const handlePerPageChange = (newPerPage: number) => {
    updatePerPage(newPerPage); // Update localStorage
    setPerPage(newPerPage);
    setCurrentPage(1);
    
    const newParams = new URLSearchParams(location.search);
    newParams.set('perPage', newPerPage.toString());
    newParams.set('page', '1');
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Filter tags for Aktuell and Archiviert
  const filterTags = [
    { label: t('general.current'), value: 'current' },
    { label: t('general.archived'), value: 'archived' },
  ];

  // Handle filter tag selection
  const handleFilterSelect = (value: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    
    if (value === 'archived') {
      newSearchParams.set('show_archived', 'true');
    } else {
      newSearchParams.delete('show_archived');
    }
    
    newSearchParams.set('page', '1'); // Reset to page 1 when changing filters
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  // Handle print functionality
  const handlePrint = () => {
    // Directly open print dialog
    // printing-residents class will only be effective in @media print
    document.body.classList.add('printing-residents');
    window.print();
    
    // Remove class after print operation completes
    // This timeout is to wait for the print dialog to close
    setTimeout(() => {
      document.body.classList.remove('printing-residents');
    }, 1000);
  };

  // Handle export as PDF
  const handleExportPdf = async () => {
    if (isExporting || !tableContainerRef.current) return;
    
    try {
      // Get today's date in ISO format for filename
      const today = new Date().toISOString().slice(0, 10);
      
      // Add class to the document body to hide loader during PDF generation
      document.body.classList.add('exporting-pdf');
      setIsExporting(true);
      
      await exportToPdf(tableContainerRef.current, {
        filename: `residents-report-${today}.pdf`,
        pageSize: 'a3',
        orientation: 'landscape',
        onStart: () => {
          setIsExporting(true);
          // We're not setting isLoading to true here to prevent loader from appearing
        },
        onComplete: () => {
          setIsExporting(false);
          // Remove the exporting-pdf class
          document.body.classList.remove('exporting-pdf');
        },
        onError: (error) => {
          console.error('Error exporting PDF:', error);
          setIsExporting(false);
          // Remove the exporting-pdf class
          document.body.classList.remove('exporting-pdf');
        }
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setIsExporting(false);
      // Make sure to remove the class in case of error
      document.body.classList.remove('exporting-pdf');
    }
  };

  // Dynamic download options
  const downloadOptions: DownloadOption[] = [
    {
      id: 'print',
      label: 'Print',
      translationKey: 'general.print',
      onClick: handlePrint
    },
    {
      id: 'pdf',
      label: 'Export as PDF',
      translationKey: 'general.exportAsPdf',
      onClick: handleExportPdf
    }
  ];

  // Generate filters for AleaListFilter component
  const filters = {
    rstate: [
      {
        label: ResidentState.ENABLED,
        value: ResidentState.ENABLED,
        id: ResidentState.ENABLED,
      },
      {
        label: ResidentState.OUT_OF_CARE,
        value: ResidentState.OUT_OF_CARE,
        id: ResidentState.OUT_OF_CARE,
      },
      {
        label: ResidentState.IN_HOSPITAL,
        value: ResidentState.IN_HOSPITAL,
        id: ResidentState.IN_HOSPITAL,
      },
      {
        label: ResidentState.DISABLED,
        value: ResidentState.DISABLED,
        id: ResidentState.DISABLED,
      },
    ],
    carehome_id: user?.role === UserRole.ORGANISATION_MANAGER ? careHomes.map((careHome) => ({
      label: careHome.name,
      value: careHome.name,
      id: careHome.id,
    })) : [],
    station_id: stations.map((station) => ({
      label: station.name,
      value: station.name,
      id: station.id,
    })),
    has_alea_set: [
      {
        label: t('assigned'),
        value: 'true',
        id: 'true',
      },
      {
        label: t('notAssigned'),
        value: 'false',
        id: 'false',
      },
    ],
    features: [
      {
        label: t('Incontinence Management (Urine)'),
        value: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
        id: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
      },
      {
        label: t('Incontinence Management (Stool)'),
        value: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
        id: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
      },
      {
        label: t('bedsoresPrevention'),
        value: ResidentFeature.REPOSITIONING_MANAGEMENT,
        id: ResidentFeature.REPOSITIONING_MANAGEMENT,
      },
      {
        label: t('safeZone'),
        value: ResidentFeature.SECURE_ZONE,
        id: ResidentFeature.SECURE_ZONE,
      },
      {
        label: t('fallDetection'),
        value: ResidentFeature.FALL_MANAGEMENT,
        id: ResidentFeature.FALL_MANAGEMENT,
      },
    ],
  };

  return (
    <Box data-testid="residents-page">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: 'center', md: 'center' }}
        mb={{ base: '20px', md: '10px' }}
        h={{ base: 'auto', md: 75 }}
        flexDirection="row"
      >
        <Heading
          size="lg"
          fontSize={25}
          fontWeight={500}
          pl={{ base: 0, md: 0 }}
          mb={{ base: 1, md: 0 }}
        >
          {t(getLanguageKeyByType('residents', user?.carehome?.type))}
        </Heading>
        {user?.role !== UserRole.ORGANISATION_MANAGER && (
          <Button
            data-testid="residents-add-button"
            leftIcon={<AddIcon />}
            colorScheme="teal"
            variant="solid"
            h="50px"
            borderRadius="20px"
            fontWeight={400}
            onClick={handleAddResidentClick}
            fontSize="20px"
          >
            {t(getLanguageKeyByType('addResident', user?.carehome?.type))}
          </Button>
        )}
      </Flex>
      
      <Box mb="25px">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={{ base: 'column', md: isLargerThan1120 ? 'row' : 'column' }}
          gap={{ base: '16px' }}
          flexWrap={'wrap'}
        >
          <AleaFilterTags
            tags={filterTags}
            selectedTags={[selectedFilter]}
            onTagSelect={handleFilterSelect}
          />
          <Flex 
            gap="10px" 
            alignItems="center" 
            position="relative"
            alignSelf="flex-start"
            width={{ base: '100%', md: 'auto' }}
          >
            <Box w={{ base: '100%', md: '320px' }}>
              <SearchBar
                onClose={handleClearSearch}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isHardwarePage={true}
                height="50px"
                placeholderLanguageKey="resident.searchPlaceholder"
              />
            </Box>
            <AleaListFilter filters={filters} />
            <AleaDownloadButton options={downloadOptions} />
          </Flex>
        </Flex>
      </Box>

      {/* Selected filter tags */}
      <AleaSelectedTags filters={filters} />

      <Box 
        bg="white" 
        borderRadius="20px" 
        overflow="auto" 
        minHeight={500} 
        ref={tableContainerRef}
        className="residents-table-container"
      >
        <ResidentsTable
          residents={residentsQuery.data?.items || []}
          isLoading={isLoading}
          stations={stations}
          total={residentsQuery.data?.total || 0}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isTableListFetched={residentsQuery.isFetched}
          selectedFilter={selectedFilter}
          careHomeFeatures={careHomeFeatures}
          usePagination={true}
          currentPage={currentPage}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      </Box>
    </Box>
  );
};

export default ResidentsPage;
